import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { useSession } from "@/services/auth0/session";

export interface BillingInfo {
  id: string;
  address: {
    line1: string;
    line2?: string;
    city: string;
    province: string;
    country: string;
    postalCode?: string;
    location: {
      lat: number;
      lng: number;
    };
    plusCode?: string;
  };
  stripeCustomerId?: string;
  paymentStyle?: string;
}

export interface BillingAddressFormData {
  line1: string;
  line2?: string;
  city: string;
  province: string;
  country: string;
  postalCode: string;
  location: {
    lat: number;
    lng: number;
  };
  plusCode?: string;
}

export function useUpdateBillingInfo({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: AxiosError<{ message: string }>) => void;
}) {
  const queryClient = useQueryClient();
  const { token, customer } = useSession();

  return useMutation({
    mutationFn: async ({
      customerId,
      data,
    }: {
      customerId: string;
      data: BillingAddressFormData;
    }) => {
      const response = await axios.patch<BillingInfo>(
        `${import.meta.env.VITE_API_URL}/api/v1/customers/${customerId}/billing`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["customer", customer?.id, token],
      });
      onSuccess?.();
    },
    onError: (error: AxiosError<{ message: string }>) => {
      onError?.(error);
    },
  });
}
